import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * VENDA IA CPF — as duas estratégias da Conecta (Douglas 26/09/2026).
 * A régua vem da config de `disparo_estrategias` e os números das filas do
 * banco (`fn_venda_ia_cpf_painel`): a tela não calcula nada por fora.
 */
export interface EtapaVendaIaCpf { ordem: number; nome: string; estado: 'pronto' | 'a_construir'; faz: string }
/**
 * Um TOP da régua. PORT e RENOV usam linhas e praça; as estratégias de lista
 * (Oi, Algar) usam o grupo da cidade, a faixa de valor e a intenção — o mesmo
 * fn_venda_ia_cpf_top_regra do banco.
 */
export interface TopVendaIaCpf {
  top: number; nome: string;
  linhas_min?: number; linhas_max?: number | null; praca?: 'MD' | 'GM' | null;
  grupos?: string[]; intencoes?: string[]; valor_min?: number; valor_menor_que?: number;
  /** como a Conecta aborda o cliente deste TOP (Oi e Algar) */
  abordagem?: string;
}
export interface AberturaVendaIaCpf {
  nome: string; rotulo: string; aprovadas: number; oficiais: number;
  /** o texto do modelo com valores de exemplo */
  exemplo?: string | null;
  /** clientes que receberam esta abertura e quantos responderam */
  enviadas?: number; responderam?: number;
}
export interface ConfigVendaIaCpf {
  retornos?: { ativo: boolean };
  limite_dia?: number;
  limite_por_linha?: number;
  so_linha_sem_custo?: boolean;
  /** Cota diária própria da linha oficial (RENOV: 50). Sem ela, a oficial é só reserva. */
  limite_oficial?: number;
  cliques_email?: { ativo: boolean; espera_minutos: number; campanha_ids: string[]; todas_campanhas?: boolean };
  limite_faturas_dia?: number;
  dias_semana?: number[];
  max_linhas?: number | null;
  margem_max?: number;
  margem_fixa_vivo?: number;
  campanha_ids?: string[];
  base_comparacao?: 'apos_promocao' | 'promocional';
  oferta?: Record<string, unknown>;
  pacote?: Record<string, unknown>;
  tops?: TopVendaIaCpf[];
  templates?: { nome: string; quando: string }[];
  telefone_gerente?: string | null;
  /** A linha que pede a conta à gerente (por padrão a da Eduarda, fora da Meta). */
  connection_id_gerente?: string | null;
  valor_min?: number;
  valor_max?: number;
  regra_simulacao?: string;
  regua?: { inicia: string; maquina_faz: string; conclui: string };
  ferramentas?: string[];
  aprovacao?: { estrategia: string; status: string; resumo: string };
  etapas?: EtapaVendaIaCpf[];
  vendedor_id?: string;
  connection_id?: string;
  agente_id?: string;
  linhas?: string[];
  /** linhas desmarcadas no card (régua de 02/10, substituída pelos canais de 03/10) */
  linhas_fora?: string[];
  /** as API oficiais que disparam o WhatsApp desta estratégia (Douglas 03/10/2026) */
  linhas_oficiais?: string[];
  /** as Evolution GO para onde vai o botão do e-mail — decidida no clique */
  linhas_retorno_email?: string[];
  /** o e-mail do Douglas sai junto com o disparo do dia (padrão: sim) */
  email_no_lote?: boolean;
  /** o SMS com o link do Douglas sai junto com o disparo (padrão: sim) */
  sms_no_disparo?: boolean;
  /** dias úteis sem resposta até a tentativa 2 e até o negado (padrão: 2 e 2) */
  regua_dias_tentativa_2?: number;
  regua_dias_negado?: number;
  prioridade_numeros?: string[];
  hora_email?: number;
  intervalo_segundos?: number;
  template_oficial?: { nome: string; variaveis?: string[] } | null;
  [chave: string]: unknown;
}
export interface EstrategiaVendaIaCpf {
  /** VENDA_IA_CPF_<X>: o registro (venda_ia_cpf_estrategias) é quem diz quais existem. */
  tipo: string;
  chave: string;
  rotulo: string;
  tipo_lead: string;
  /** false: os leads nascem, mas o disparo e a Conecta ainda não a conhecem — não liga. */
  disparo_pronto: boolean;
  /** Estratégia de lista do dia (Oi, Algar): os leads chegam pelas remessas. */
  lista: boolean;
  origens: string[];
  criados_hoje: number;
  criados_total: number;
  ultima_carga: string | null;
  /** As ferramentas ativas da Conecta, lidas do cadastro dela. */
  ferramentas: string[];
  /** As aberturas que o disparador usa, com em quantas oficiais o modelo está aprovado. */
  aberturas: AberturaVendaIaCpf[];
  nome: string;
  ativo: boolean;
  config: ConfigVendaIaCpf;
  janela_inicio: number;
  janela_fim: number;
  na_fila: number;
  por_top: { top: number; na_fila: number; md: number }[];
  enviados_hoje: number;
  enviados_7d: number;
  responderam_7d: number;
  faturas_pedidas_hoje: number;
  erros_7d: number;
  vendas_7d: number;
  /** E-mails da estratégia (o disparo do CNPJ irregular que sai como CPF) */
  emails_hoje: number;
  emails_7d: number;
  emails_responderam_7d: number;
  campanhas: { id: string; nome: string; leads: number }[];
}
export interface CampanhaCpf { id: string; nome: string; tipo_lead: string; leads: number }
export interface PreviaVendaIaCpf {
  estrategia: string; top: number | null; praca: string | null; linhas: number | null;
  valor_hoje: number | null; valor_pacote: number | null; diferenca: number | null;
  cobertura: boolean | null; fixa_vivo: boolean | null; tipo_cliente: string | null;
  pacote: { item: string; valor: number; detalhe?: string | string[] }[];
  entra: boolean; motivo: string | null; calculado_em: string; campanha: string | null;
  grupo?: string | null;
}

export async function lerDoCrm<T>(caminho: string, erroPadrao: string, metodo: 'GET' | 'POST' = 'GET'): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente.');
  const base = ((import.meta.env.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');
  const r = await fetch(`${base}${caminho}`, { method: metodo, headers: { Authorization: `Bearer ${session.access_token}` } });
  const corpo = await r.json().catch(() => ({ error: 'O CRM não respondeu corretamente.' }));
  if (!r.ok) throw new Error(corpo.error || erroPadrao);
  return corpo as T;
}

/** A régua mudou: recalcula a prévia (TOP, pacote, diferença) no banco. */
export function recalcularPreviaVendaIaCpf() {
  return lerDoCrm<{ recalculados: { port: number; renov: number } }>('/v1/venda-ia-cpf/recalcular', 'Não foi possível recalcular a prévia.', 'POST');
}

export function useVendaIaCpfCampanhas() {
  return useQuery({
    queryKey: ['venda-ia-cpf', 'campanhas'],
    staleTime: 60_000,
    queryFn: async () => (await lerDoCrm<{ campanhas: CampanhaCpf[] }>('/v1/venda-ia-cpf/campanhas', 'Não foi possível carregar as campanhas CPF.')).campanhas,
  });
}

export function usePreviaVendaIaCpf(leadId: string | null | undefined) {
  return useQuery({
    queryKey: ['venda-ia-cpf', 'previa', leadId],
    enabled: !!leadId,
    staleTime: 60_000,
    queryFn: async (): Promise<PreviaVendaIaCpf | null> => {
      const { previa } = await lerDoCrm<{ previa: PreviaVendaIaCpf | null }>(`/v1/venda-ia-cpf/previa/${leadId}`, 'Não foi possível carregar a prévia.');
      if (!previa) return null;
      const n = (v: unknown) => (v === null || v === undefined ? null : Number(v));
      return { ...previa, top: n(previa.top), linhas: n(previa.linhas), valor_hoje: n(previa.valor_hoje),
        valor_pacote: n(previa.valor_pacote), diferenca: n(previa.diferenca),
        pacote: (previa.pacote || []).map((p) => ({ ...p, valor: Number(p.valor) })) };
    },
  });
}

/**
 * A ABERTURA DO LEAD DA OI OU DA ALGAR (Douglas 02/10/2026): qual mensagem
 * aprovada ele recebe, em qual telefone e com que oferta — a mesma régua do
 * disparo (fn_venda_ia_cpf_abertura_lista).
 */
export interface AberturaListaCpf {
  modelo?: string; fora?: string; rotulo?: string | null; texto?: string | null;
  telefone?: string; operadora?: string; operadora_consultada?: string; grupo?: string; intencao?: string;
  numeros?: { numero: string; operadora: string }[]; celulares?: number; fixos?: number;
  valor_oi?: number; velocidade_hoje?: string;
  fibra?: { codigo: string; internet: string; valor: number; meses?: number; valor_depois: number };
  celular?: { codigo: string; valor: number; nome: string };
  total?: number; total_depois?: number; economia?: number;
  top?: number | null; campanha?: string | null;
}

export function useAberturaListaCpf(leadId: string | null | undefined, ativo = true) {
  return useQuery({
    queryKey: ['venda-ia-cpf', 'abertura', leadId],
    enabled: !!leadId && ativo,
    staleTime: 60_000,
    queryFn: async () =>
      (await lerDoCrm<{ abertura: AberturaListaCpf | null }>(`/v1/venda-ia-cpf/abertura/${leadId}`, 'Não foi possível carregar a abertura do lead.')).abertura,
  });
}

export interface ExemploMapaPort {
  lead_id: string; cidade: string | null; uf: string | null; grupo: string | null; linhas: number | null;
  paga_hoje: number | null; pacote: number | null; diferenca: number | null; motivo: string | null;
  movel: string | null; fibra: string | null;
}
export interface LinhaMapaPort {
  top: number | null; tipo: string; leads: number; entram: number;
  fora_preco: number; fora_cobertura: number; fora_empresa_ativa: number; exemplos: ExemploMapaPort[];
}

/** O mapa da estratégia (PORT ou RENOV): TOP × tipo de proposta, com exemplos reais da prévia. */
export function useMapaPortCpf(estrategia: 'VENDA_IA_CPF_PORTABILIDADE' | 'VENDA_IA_CPF_RENOVACAO' = 'VENDA_IA_CPF_PORTABILIDADE') {
  return useQuery({
    queryKey: ['venda-ia-cpf', 'mapa', estrategia],
    staleTime: 120_000,
    queryFn: async (): Promise<LinhaMapaPort[]> => {
      const { mapa } = await lerDoCrm<{ mapa: LinhaMapaPort[] }>(`/v1/venda-ia-cpf/mapa-port?estrategia=${estrategia}`, 'Não foi possível carregar o mapa da estratégia.');
      const n = (v: unknown) => (v === null || v === undefined ? null : Number(v));
      return (mapa || []).map((m) => ({
        ...m, top: n(m.top), leads: Number(m.leads), entram: Number(m.entram), fora_preco: Number(m.fora_preco),
        fora_cobertura: Number(m.fora_cobertura), fora_empresa_ativa: Number(m.fora_empresa_ativa),
        exemplos: (m.exemplos || []).map((e) => ({ ...e, linhas: n(e.linhas), paga_hoje: n(e.paga_hoje), pacote: n(e.pacote), diferenca: n(e.diferenca) })),
      }));
    },
  });
}

export function useVendaIaCpf() {
  return useQuery({
    queryKey: ['venda-ia-cpf', 'painel'],
    staleTime: 30_000,
    refetchInterval: 120_000,
    queryFn: async (): Promise<EstrategiaVendaIaCpf[]> => {
      const corpo = await lerDoCrm<{ estrategias?: EstrategiaVendaIaCpf[] }>('/v1/venda-ia-cpf/painel', 'Não foi possível carregar a Venda IA CPF.');
      return ((corpo.estrategias || []) as EstrategiaVendaIaCpf[]).map((e) => ({
        ...e,
        na_fila: Number(e.na_fila), enviados_hoje: Number(e.enviados_hoje), enviados_7d: Number(e.enviados_7d),
        responderam_7d: Number(e.responderam_7d), faturas_pedidas_hoje: Number(e.faturas_pedidas_hoje),
        erros_7d: Number(e.erros_7d), vendas_7d: Number(e.vendas_7d),
        emails_hoje: Number(e.emails_hoje ?? 0), emails_7d: Number(e.emails_7d ?? 0),
        emails_responderam_7d: Number(e.emails_responderam_7d ?? 0),
        por_top: (e.por_top || []).map((t) => ({ top: Number(t.top), na_fila: Number(t.na_fila), md: Number(t.md) })),
        campanhas: (e.campanhas || []).map((c) => ({ ...c, leads: Number(c.leads) })),
        disparo_pronto: e.disparo_pronto !== false, lista: !!e.lista, origens: e.origens || [],
        criados_hoje: Number(e.criados_hoje ?? 0), criados_total: Number(e.criados_total ?? 0), ultima_carga: e.ultima_carga ?? null,
        ferramentas: e.ferramentas || [],
        aberturas: (e.aberturas || []).map((a) => ({ ...a, aprovadas: Number(a.aprovadas), oficiais: Number(a.oficiais) })),
      }));
    },
  });
}

export interface CpfSocio { tem_cpf: boolean; cnpj: string | null; status_assertiva: string | null }

/** O lead da empresa baixada já tem o CPF do sócio? E a consulta na Assertiva, em que pé está? */
export function useCpfSocioVendaIaCpf(leadId: string | null) {
  return useQuery({
    queryKey: ['venda-ia-cpf', 'cpf-socio', leadId],
    enabled: !!leadId,
    staleTime: 15_000,
    refetchInterval: (q) => (['PENDENTE', 'PROCESSANDO'].includes(q.state.data?.status_assertiva ?? '') ? 10_000 : false),
    queryFn: async () => (await lerDoCrm<{ cpf_socio: CpfSocio | null }>(
      `/v1/venda-ia-cpf/cpf-socio/${leadId}`, 'Não foi possível ver o CPF do sócio.')).cpf_socio,
  });
}

export function buscarCpfSocioVendaIaCpf(leadId: string) {
  return lerDoCrm<{ status: string }>(`/v1/venda-ia-cpf/cpf-socio/${leadId}`, 'Não foi possível pedir o CPF à Assertiva.', 'POST');
}

export interface LinhaDisparador {
  id: string; nome: string; provider: string; telefone: string | null; de_quem: string | null; conecta_responde: boolean; aviso: string | null;
  /** a conexão está aberta agora (whatsapp_connection_health) */
  de_pe?: boolean;
}

/** As linhas que o disparador pode usar (equipe CONECTA ou do Douglas), com quem responde em cada uma. */
export function useVendaIaCpfLinhas() {
  return useQuery({
    queryKey: ['venda-ia-cpf', 'linhas'],
    staleTime: 60_000,
    queryFn: async () => (await lerDoCrm<{ linhas: LinhaDisparador[] }>('/v1/venda-ia-cpf/linhas', 'Não foi possível carregar as linhas.')).linhas,
  });
}

/** O log do ciclo (Douglas 28/09/2026): cada lead trabalhado e cada passo, da conta ao pedido. */
export interface PassoEnvioVendaIaCpf { status: string; quando: string; erro: string | null; numero?: string | null; origem_numero?: string | null; modelo?: string | null }
export interface LogVendaIaCpf {
  lead_id: string; lead_numero: number; nome: string | null; status: string; motivo_negado: string | null; top: number | null;
  inicio: string; ultimo: string;
  conta_pedida: string | null; conta_recebida: string | null; conta_nao_existe: boolean; conta_lida: string | null;
  email: PassoEnvioVendaIaCpf | null; whatsapp: PassoEnvioVendaIaCpf | null;
  clique: string | null; aviso_clique: string | null; respondeu: string | null;
  negociando: string | null; proposta: string | null; pedido: string | null; carteirizado: string | null; negado: string | null;
  problema: string | null;
}

export function useLogVendaIaCpf(tipo: EstrategiaVendaIaCpf['tipo'] | null) {
  return useQuery({
    queryKey: ['venda-ia-cpf', 'log', tipo],
    enabled: !!tipo,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async () => (await lerDoCrm<{ log: LogVendaIaCpf[] }>(`/v1/venda-ia-cpf/log?tipo=${tipo}`, 'Não foi possível carregar o log do ciclo.')).log,
  });
}

export interface CliquesEmailVendaCpf {
  campanhas: { id: string; nome: string; criada_em: string; destino: string }[];
  clientes: { lead_id: string; lead_numero: string; nome: string; campanha_nome: string; clicado_em: string;
    cliques: string; status_envio: string | null; erro: string | null; motivo: string | null }[];
}

export function useCliquesEmailVendaCpf(ativo: boolean) {
  return useQuery({
    queryKey: ['venda-ia-cpf', 'cliques-email'],
    enabled: ativo,
    refetchInterval: 60_000,
    queryFn: () => lerDoCrm<CliquesEmailVendaCpf>('/v1/venda-ia-cpf/cliques-email', 'Não foi possível carregar os cliques da campanha.'),
  });
}
